/* eslint-disable no-restricted-globals */
import classNames from 'classnames';
import { messageLog } from 'utilities/helpers/logger/message-logger.js';

export type AdUtilsType = ReturnType<typeof AdUtilsInit>;

const TOOL_TIP_SELECTOR = 'takeover-tooltip';

export const AdUtilsInit = () => {
  let isMapTakeoverDisplaying: boolean = false;
  let documentRef: null | Document = null;
  const classHistory: Map<string, string> = new Map();
  const setRef = (docRef: Document) => {
    documentRef = docRef;
  };

  const _getSelector = (containerSelector: string): HTMLElement | null => {
    return documentRef?.querySelector(containerSelector) ?? document.querySelector(containerSelector);
  };
  const _getSelectorAll = (containerSelector: string): NodeListOf<Element> => {
    const docRef = documentRef?.querySelectorAll(containerSelector);
    if (docRef && docRef?.length > 0) {
      return docRef;
    }
    return document.querySelectorAll(containerSelector);
  };

  const _deleteEl = (elementSelector: string) => {
    const element = _getSelector(elementSelector);
    if (element && element.parentNode) {
      element.parentNode.removeChild(element);
    }
    messageLog.ads(`AdUtils: DeleteElement`, { args: [[`ElementSelector ${elementSelector}`, element]] });
  };
  /**
   * This is to allow ads to be moved outside of their container e.g. with mapTakeovers
   * @param containerSelector
   * @param targetSelector
   */
  const _moveAd = (containerSelector: string, targetSelector: string) => {
    const elToMove = _getSelector(containerSelector);
    const target = _getSelector(targetSelector);
    if (!target || !elToMove) {
      return;
    }

    messageLog.ads(`AdUtils: MoveAd`, {
      args: [
        [`ContainerEl ${containerSelector}`, elToMove],
        [`NewParentEl ${targetSelector}`, target],
      ],
    });

    target.appendChild(elToMove);
  };

  const addClass = (elementSelector: string, className: string) => {
    const element = _getSelector(elementSelector);
    if (element) {
      element.classList.add(className);
      classHistory.set(elementSelector, className);
    }
    messageLog.ads(`AdUtils: AddClass`, { args: [[`NewElementSelector ${elementSelector}`, element]] });
  };

  const _reinstateClasses = () => {
    classHistory.forEach((className, elementSelector) => {
      const element = _getSelector(elementSelector);
      if (element) {
        element.classList.remove(className);
      }
      messageLog.ads(`AdUtils: ReinstateClasses`, { args: [[`ElementSelector ${elementSelector}`, element]] });
    });
    classHistory.clear();
  };

  const _removeToolTips = () => {
    const toolTips = _getSelectorAll(`.${TOOL_TIP_SELECTOR}`);
    toolTips.forEach((tip) => {
      if (tip.parentNode) {
        tip.parentNode.removeChild(tip);
      }
    });
    messageLog.ads(`AdUtils: RemoveToolTips`, { args: [[`ToolTips`, toolTips]] });
  };

  const createToolTipDiv = (classes?: string): HTMLDivElement => {
    const div = document.createElement('div');
    div.className = classNames(`${TOOL_TIP_SELECTOR} u-posAbs pill--ad u-pointerHide`, classes);
    return div;
  };

  const createToolTipLink = (classes: string, desiredLink: string): HTMLAnchorElement => {
    const a = document.createElement('a');
    a.className = classNames(`${TOOL_TIP_SELECTOR} u-posAbs pill--ad u-pointerShow`, classes);
    a.setAttribute('href', desiredLink);
    a.setAttribute('target', '_blank');
    return a;
  };

  const addAdvertMapIcon = (
    _div: HTMLDivElement,
    _townSelector: string,
    position: Partial<Record<'right' | 'left' | 'top', number>> = {},
  ) => {
    /** Create All Element Function */
    const div = _div.cloneNode(true) as HTMLDivElement;
    if (position.right) {
      div.style.right = `${position.right}px`;
    }
    if (position.left) {
      div.style.left = `${position.left}px`;
    }
    if (position.top) {
      div.style.top = `${position.top}px`;
    }
    const townSelector = _townSelector.toLowerCase().replace(' ', '-');
    _getSelectorAll(`.Map-cartography-image  [data-takeover-id=${townSelector}] .pill-v2--hover-listen`).forEach(
      (el) => {
        const existingAdEl = el.querySelector('div.Ad-tooltip-content');
        /** Only add if not already there */
        if (!existingAdEl) {
          el.appendChild(div);
        }
      },
    );
  };

  const _toggleAd = (display: boolean) => {
    //just for reference, top is the top document, self is the current document and parent is the document above the current one.
    const haystack = document.getElementsByTagName('IFRAME') as unknown as Window[];
    const needle = documentRef?.defaultView;
    messageLog.ads(`AdUtils: ${display ? 'showAd' : 'hideAd'}`, {
      args: [
        ['IFrames', haystack],
        ['defaultView', needle],
      ],
    });
    for (const frame of haystack) {
      if (frame.name === needle?.name) {
        const val = document.getElementById(frame.name);
        const status = display ? 'block' : 'hide';
        if (val) {
          val.style.display = status;
        }
      }
    }
  };

  /** Displays map takeover */
  const hideAd = () => {
    if (isMapTakeoverDisplaying) {
      messageLog.ads(`AdUtils: Calling hideAd when already displaying map takeover`);
      return;
    }
    _moveAd('.map-takeover-wrapper', '.Map-cartography');
    _toggleAd(false);
    isMapTakeoverDisplaying = true;
  };

  const _showAd = () => {
    /** Delete moved ad */
    _deleteEl('.Map-cartography .map-takeover-wrapper');
    _toggleAd(true);
    isMapTakeoverDisplaying = false;
  };

  const clearMapTakeover = () => {
    if (isMapTakeoverDisplaying) {
      _reinstateClasses();
      _showAd();
      _removeToolTips();
    }
  };

  return {
    setRef,
    addClass,
    hideAd,
    getIsMapTakeoverDisplaying: () => isMapTakeoverDisplaying,
    resetMapTakeover: () => (isMapTakeoverDisplaying = false),
    clearMapTakeover,
    addAdvertMapIcon,
    createToolTipDiv,
    createToolTipLink,
  };
};
